import Component from "@base/Component.js";
import config from "@/config.js";
import t from "@/services/translationService.js";
import Hero from "./Hero.js";
import { foldTag as fold } from "@/services/tags.js";

/**
 * Gallery grid with a tag search bar. Renders a card per gallery (cover + title +
 * tags). Selecting tags filters the grid (client-side, AND semantics: a gallery
 * must carry every selected tag).
 *
 * The filter is a typeahead, not a chip wall: the tag vocabulary runs to dozens
 * of entries and rendering them all overflowed .gallery-controls, whose height is
 * budgeted so header + hero + this band add up to one viewport. A search input is
 * one line tall no matter how many tags exist, and its suggestion list is
 * absolutely positioned so opening it never reflows the grid.
 *
 * Suggestions are drawn from the *currently filtered* galleries and carry their
 * counts, so every suggestion narrows to at least one gallery — with AND
 * semantics, offering a tag that yields nothing is a dead end.
 *
 * View-only: creating and editing galleries lives in the Cockpit
 * (#/cockpit/galleries). The draft/private badges stay, since the backend only
 * ever sends galleries the viewer is allowed to see.
 *
 * Galleries store tag *keys*; `tagLabels` maps each to its name in the active
 * language (see tagManager). Selection, filtering and the URL-free state all
 * work on keys — only what the visitor reads is translated, so switching
 * language keeps the filter intact.
 *
 * props: { galleries: GalleryObject[], lang, heroImage, tagLabels: {key:label}, onOpen }
 */
export default class GalleryPanel extends Component {
  constructor(props) {
    super(props);
    this.state.selected = new Set(); // active tag filters
    this.state.query = ""; // typeahead text, preserved across re-renders
    this.state.focusSearch = false; // re-focus the input after a commit re-render
    this.highlight = -1; // keyboard cursor into the suggestion list (transient)
  }

  /** Display name for a tag key in the active language, or the key itself. */
  #label(key) {
    return this.props.tagLabels?.[key] || key;
  }

  #filtered() {
    const sel = this.state.selected;
    if (!sel.size) return this.props.galleries;
    // AND: a gallery must carry every selected tag.
    return this.props.galleries.filter((g) => {
      const tags = g.tagList();
      return [...sel].every((tag) => tags.includes(tag));
    });
  }

  /**
   * Tags available to add, with how many of the currently-visible galleries
   * carry each. Ranked prefix-matches first, then by count, then alphabetically —
   * typing "la" should surface "Landscape" above "Highland".
   */
  #suggestions() {
    const sel = this.state.selected;
    const q = fold(this.state.query.trim());
    const counts = new Map();
    for (const g of this.#filtered()) {
      for (const tag of g.tagList()) {
        if (sel.has(tag)) continue;
        counts.set(tag, (counts.get(tag) || 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([tag, count]) => {
        const label = this.#label(tag);
        // Both spellings are searchable: a German visitor types "Landschaft",
        // but the tag someone bookmarked or typed from memory is "Landscape".
        return { tag, label, count, folded: [...new Set([fold(label), fold(tag)])] };
      })
      .filter(({ folded }) => !q || folded.some((f) => f.includes(q)))
      .sort((a, b) => {
        const aPrefix = q && a.folded.some((f) => f.startsWith(q));
        const bPrefix = q && b.folded.some((f) => f.startsWith(q));
        if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
        if (a.count !== b.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      });
  }

  /** Suggestion list markup. Rebuilt in place while typing, so it stays a pure function. */
  #suggestionMarkup() {
    const items = this.#suggestions();
    if (!items.length) {
      return `<li class="tag-search__empty">${Component.escape(t.t("gallery.noTagMatch"))}</li>`;
    }
    return items
      .map(
        ({ tag, label, count }, i) =>
          `<li><button type="button" class="tag-search__option ${i === this.highlight ? "tag-search__option--active" : ""}" role="option" aria-selected="${i === this.highlight}" data-tag="${Component.escape(tag)}">
             <span class="tag-search__option-label">${Component.escape(label)}</span>
             <span class="tag-search__option-count">${count}</span>
           </button></li>`
      )
      .join("");
  }

  render() {
    const { lang } = this.props;

    const heroSlot = `<div class="hero-slot"></div>`;

    if (!this.props.galleries.length) {
      return `<section class="gallery-page">${heroSlot}<div class="gallery-controls"></div><div class="gallery-grid gallery-grid--empty"><p>${t.t("gallery.empty")}</p></div></section>`;
    }

    const sel = this.state.selected;
    const hasTags = this.props.galleries.some((g) => g.tagList().length);

    const selectedChips = [...sel]
      .map((tag) => ({ tag, label: this.#label(tag) }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(
        ({ tag, label }) => `<button type="button" class="tag-pill" data-tag="${Component.escape(tag)}" aria-label="${Component.escape(t.t("gallery.removeTag"))} ${Component.escape(label)}">
             <span>${Component.escape(label)}</span><span class="tag-pill__x" aria-hidden="true">×</span>
           </button>`
      )
      .join("");

    const clearAll =
      sel.size > 1
        ? `<button type="button" class="tag-clear" data-action="clear">${Component.escape(t.t("gallery.clearFilters"))}</button>`
        : "";

    const filterBar = hasTags
      ? `<div class="gallery-filter" role="search" aria-label="${Component.escape(t.t("gallery.filterLabel"))}">
           <div class="tag-search">
             <input type="text" class="tag-search__input" role="combobox" aria-expanded="false"
                    aria-controls="tag-suggestions" aria-autocomplete="list" autocomplete="off"
                    placeholder="${Component.escape(t.t("gallery.searchPlaceholder"))}"
                    value="${Component.escape(this.state.query)}" />
             <ul class="tag-search__suggestions" id="tag-suggestions" role="listbox" hidden></ul>
           </div>
           <div class="gallery-filter__selected">${selectedChips}${clearAll}</div>
         </div>`
      : "";

    const galleries = this.#filtered();
    const cards = galleries
      .map((g) => {
        const cover = g.coverImage();
        const img = cover
          ? `<img class="gallery-card__img" src="${config.imageBase}/${encodeURIComponent(g.galleryId)}/${encodeURIComponent(cover)}?size=thumb" alt="${Component.escape(g.title(lang))}" loading="lazy" />`
          : `<div class="gallery-card__img gallery-card__img--placeholder"></div>`;
        const badge = g.isPublic()
          ? ""
          : g.isDraft()
          ? `<span class="gallery-card__badge">${Component.escape(t.t("gallery.draft"))}</span>`
          : `<span class="gallery-card__badge">${Component.escape(t.t("gallery.private"))}</span>`;
        const cardTags = g.tagList().length
          ? `<div class="gallery-card__tags">${g
              .tagList()
              .map((tag) => `<button class="gallery-card__tag ${sel.has(tag) ? "gallery-card__tag--active" : ""}" data-tag="${Component.escape(tag)}">${Component.escape(this.#label(tag))}</button>`)
              .join("")}</div>`
          : "";
        return `
          <article class="gallery-card" data-id="${Component.escape(g.galleryId)}">
            <div class="gallery-card__media" role="button" tabindex="0">${img}${badge}</div>
            <div class="gallery-card__body">
              <h2 class="gallery-card__title">${Component.escape(g.title(lang))}</h2>
              <p class="gallery-card__subtitle">${Component.escape(g.subtitle(lang))}</p>
              ${cardTags}
            </div>
          </article>`;
      })
      .join("");

    const grid = galleries.length
      ? `<div class="gallery-grid">${cards}</div>`
      : `<div class="gallery-grid gallery-grid--empty"><p>${t.t("gallery.noMatch")}</p></div>`;

    return `<section class="gallery-page">${heroSlot}<div class="gallery-controls">${filterBar}</div>${grid}</section>`;
  }

  onMounted() {
    this.mountChild(Hero, { heroImage: this.props.heroImage ?? null }, this.$(".hero-slot"));

    const open = (id) => id && this.props.onOpen?.(id);

    this.$$(".gallery-card").forEach((card) => {
      const id = card.getAttribute("data-id");
      const media = card.querySelector(".gallery-card__media");
      this.listen(media, "click", () => open(id));
      this.listen(media, "keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open(id);
        }
      });
    });

    // Clicking a tag on a card toggles that tag filter — the primary way the
    // vocabulary gets discovered, so it stays even though the wall of chips went.
    this.$$(".gallery-card__tag").forEach((tagEl) =>
      this.listen(tagEl, "click", (e) => {
        e.stopPropagation();
        this.#toggleTag(tagEl.getAttribute("data-tag"));
      })
    );

    this.$$(".tag-pill").forEach((pill) => this.listen(pill, "click", () => this.#toggleTag(pill.getAttribute("data-tag"))));
    const clear = this.$(".tag-clear");
    if (clear) this.listen(clear, "click", () => this.#clearTags());

    this.#wireSearch();
  }

  #wireSearch() {
    const input = this.$(".tag-search__input");
    if (!input) return;

    // Typing must NOT go through setState: it replaces the component's element
    // outright, which would drop focus and the caret on every keystroke (and
    // re-mount the Hero child). The list is patched in place instead; only
    // committing a tag, which actually changes the grid, re-renders.
    this.listen(input, "input", () => {
      this.state.query = input.value;
      this.highlight = -1;
      this.#renderSuggestions(true);
    });
    this.listen(input, "focus", () => this.#renderSuggestions(true));
    this.listen(input, "keydown", (e) => this.#onSearchKey(e));

    // Delegated so rebuilding the list's innerHTML never needs new listeners.
    // `mousedown` beats the input's blur, which would close the list first.
    const list = this.$(".tag-search__suggestions");
    this.listen(list, "mousedown", (e) => {
      const option = e.target.closest(".tag-search__option");
      if (!option) return;
      e.preventDefault();
      this.#commitTag(option.getAttribute("data-tag"));
    });

    this.listen(document, "click", (e) => {
      if (!this.$(".tag-search")?.contains(e.target)) this.#closeSuggestions();
    });

    if (this.state.focusSearch) {
      this.state.focusSearch = false;
      input.focus({ preventScroll: true });
      this.#renderSuggestions(true);
    }
  }

  #renderSuggestions(show) {
    const list = this.$(".tag-search__suggestions");
    const input = this.$(".tag-search__input");
    if (!list || !input) return;
    list.innerHTML = this.#suggestionMarkup();
    list.hidden = !show;
    input.setAttribute("aria-expanded", String(Boolean(show)));
  }

  #closeSuggestions() {
    const list = this.$(".tag-search__suggestions");
    if (!list || list.hidden) return;
    list.hidden = true;
    this.highlight = -1;
    this.$(".tag-search__input")?.setAttribute("aria-expanded", "false");
  }

  #onSearchKey(e) {
    const items = this.#suggestions();

    if (e.key === "Escape") {
      this.#closeSuggestions();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!items.length) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      this.highlight = (this.highlight + step + items.length) % items.length;
      this.#renderSuggestions(true);
      // `?.` on the method too: jsdom (and older engines) do not implement it.
      this.$(".tag-search__option--active")?.scrollIntoView?.({ block: "nearest" });
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      // No explicit cursor: commit the top hit, so typing a tag and hitting
      // Enter works without ever touching the arrow keys.
      const pick = items[this.highlight >= 0 ? this.highlight : 0];
      if (pick) this.#commitTag(pick.tag);
      return;
    }
    if (e.key === "Backspace" && !this.state.query && this.state.selected.size) {
      // Backspace on an empty field peels off the last tag, as chip inputs do.
      e.preventDefault();
      this.#toggleTag([...this.state.selected].pop());
    }
  }

  /** Add a tag from the typeahead, clear the query, and keep the caret in the field. */
  #commitTag(tag) {
    if (!tag) return;
    this.state.selected.add(tag);
    this.setStateKeepingPageScroll({ query: "", focusSearch: true });
  }

  #toggleTag(tag) {
    const sel = this.state.selected;
    if (!tag) return;
    if (sel.has(tag)) sel.delete(tag);
    else sel.add(tag);
    this.setStateKeepingPageScroll({ query: "" });
  }

  #clearTags() {
    this.state.selected.clear();
    this.setStateKeepingPageScroll({ query: "" });
  }
}
